<template>
  <div class="changelog-container">
    <!-- Loading State -->
    <div
      v-if="issuesLoading && changelog.length === 0"
      class="loading-wrapper"
    >
      <div class="loading-spinner" />
      <p class="loading-text">
        {{ $t('changelog.loadingIssues', 'Loading issues...') }}
      </p>
    </div>

    <!-- Error State -->
    <div
      v-else-if="false"
      class="error-state"
    >
      <p class="error-text">
        {{ $t('changelog.projectTagsQueryError', 'Error loading changelog data') }}
      </p>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="changelog.length === 0"
      class="empty-state"
    >
      <div class="empty-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
      </div>
      <p class="empty-text">
        {{ $t('changelog.noChanges', 'No changelog data available') }}
      </p>
    </div>

    <!-- Changelog Content -->
    <div
      v-else
      class="changelog-content"
    >
      <div class="changelog-tags">
        <div
          v-for="tag in changelog"
          :key="`changelog_tag_${tag.id}`"
          class="tag-card"
        >
          <!-- Tag Header -->
          <div class="tag-header">
            <svg
              class="tag-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
              />
            </svg>

            <div class="tag-info">
              <span class="tag-title">{{ tag.name }}</span>
              <span class="tag-date">{{ formatTagDate(tag.createdAt) }}</span>
            </div>
          </div>

          <!-- Issues List -->
          <ul class="issues-list">
            <li
              v-if="tag.issues.length === 0"
              class="no-issues"
            >
              {{ $t('changelog.noIssues', 'No issues for this tag') }}
            </li>

            <li
              v-for="issue in tag.issues"
              :key="`changelog_issue_${issue.id}_${tag.id}`"
              class="issue-item"
            >
              <div class="issue-content">
                <div class="issue-avatar">
                  <span class="avatar-text">
                    {{ (issue.author_name || '?').charAt(0).toUpperCase() }}
                  </span>
                </div>

                <div class="issue-details">
                  <p class="issue-title">
                    {{ issue.title }}
                  </p>

                  <div class="issue-meta">
                    <span
                      v-if="issue.author_name"
                      class="issue-author"
                    >
                      {{ issue.author_name }}
                    </span>
                    <a
                      v-if="issue.web_url"
                      :href="issue.web_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="issue-link"
                    >
                      <svg
                        class="link-icon"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                      {{ $t('changelog.viewIssue', 'View Issue') }}
                    </a>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- Load More Button -->
      <div class="load-more-section">
        <button
          type="button"
          :disabled="issuesLoading"
          class="load-more-btn"
          @click="loadMore"
        >
          <span v-if="issuesLoading">Loading...</span>
          <span v-else>{{ $t('changelog.loadMore', 'Load More') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue } from 'vue-property-decorator'
import Component from 'vue-class-component'
import { Issue, IssuesQuery, ProjectTagsQuery } from '@/types/graphql'
import { ApolloQueryResult } from 'apollo-client'
import { formatDateOnly } from '@/lib/date'

type ChangelogData = {
  id: string
  name: string
  issues: Issue[]
}[];

@Component({
  components: {
  }
})

export default class Changelog extends Vue {
  // ➊ Props

    // ➋ Data
    private changelog: ChangelogData = [];

    private page = 1;

    private pageSize = 5;

    private tagsLoading = false;

    private issuesLoading = false;

    // ➌ Computed properties

    // ➍ Watchers

    // ➎ Methods
    private async fetchChangelog() {
      this.issuesLoading = true

      try {
        const tagsResult: ApolloQueryResult<ProjectTagsQuery> = await this.$apollo.query({
          query: ProjectTagsQuery,
          variables: {
            page: this.page,
            perPage: this.pageSize
          },
          fetchPolicy: 'network-only'
        })

        if (!tagsResult || !tagsResult.data || !tagsResult.data.projectTags) {
          return
        }

        let tags = tagsResult.data.projectTags.nodes.map((tag) => ({
          id: tag.id,
          name: tag.name,
          createdAt: tag.createdAt,
          issues: [] as Issue[]
        }))

        for (const tag of tags) {
          let issuesCurrentPage = 1
          let hasNextPage = true

          while (hasNextPage) {
            const result = await this.$apollo.query({
              query: IssuesQuery,
              variables: {
                tagName: tag.name,
                page: issuesCurrentPage,
                perPage: 100
              },
              fetchPolicy: 'network-only'
            })

            if (result.data?.issues?.nodes && result.data.issues.nodes.length > 0) {
              tags = tags.map((t) => {
                if (t.id === tag.id) {
                  const allIssues: Issue[] = [
                    ...(t.issues || []),
                    ...(result.data?.issues?.nodes || [])
                  ]

                  const appValues = [
                    'client-dash',
                    'client dash',
                    'handesk',
                    'helpdesk'
                  ]

                  const appIssues = allIssues.filter(issue =>
                    appValues.some(value => issue.title.toLowerCase().includes(value))
                  )

                  const otherIssues = allIssues.filter(issue =>
                    !appValues.some(value => issue.title.toLowerCase().includes(value))
                  )

                  appIssues.sort((a, b) => {
                    const nameA = a.title.toLowerCase()
                    const nameB = b.title.toLowerCase()

                    // Sort the rest by title alphabetically
                    if (nameA < nameB) return -1
                    if (nameA > nameB) return 1

                    return 0
                  })

                  otherIssues.sort((a, b) => {
                    const nameA = a.title.toLowerCase()
                    const nameB = b.title.toLowerCase()

                    // Sort the rest by title alphabetically
                    if (nameA < nameB) return -1
                    if (nameA > nameB) return 1

                    return 0
                  })

                  return {
                    ...t,
                    issues: [
                      ...appIssues,
                      ...otherIssues
                    ]
                  }
                }

                return t
              })
            }

            hasNextPage = result.data?.issue?.paginatorInfo?.hasMorePages || false
            issuesCurrentPage += 1
          }
        }

        this.changelog = [
          ...this.changelog,
          ...tags.map(tag => ({
            id: tag.id,
            name: tag.name,
            createdAt: tag.createdAt,
            issues: tag.issues
          }))
        ]
      } catch (error) {
        console.error('Error fetching changelog:', error)
      } finally {
        this.issuesLoading = false
      }
    }

    private loadMore() {
      this.page += 1
      this.fetchChangelog()
    }

    private formatTagDate(date: Date) {
      return formatDateOnly(date)
    }

    // ➏ Lifecycle hooks
    private mounted() {
      this.fetchChangelog()
    }
}
</script>

<style lang="scss" scoped>
.changelog-container {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  color: #f3f4f6;
  overflow: auto;
  padding: 1rem;
  background-color: #111827;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid #374151;
}

.loading-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  margin: 2.5rem 0;

  .loading-spinner {
    width: 3rem;
    height: 3rem;
    border: 3px solid #374151;
    border-top: 3px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .loading-text {
    margin-top: 1rem;
    color: #9ca3af;
    font-size: 0.875rem;
  }
}

.error-state {
  border-radius: 0.5rem;
  padding: 1.5rem;
  border: 1px solid #dc2626;
  background-color: rgba(153, 27, 27, 0.3);
  text-align: center;

  .error-text {
    color: #fca5a5;
    font-weight: 500;
    margin: 0;
  }
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  text-align: center;

  .empty-icon {
    width: 4rem;
    height: 4rem;
    margin: 0 auto;
    color: #4b5563;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .empty-text {
    margin-top: 1rem;
    color: #9ca3af;
    font-size: 1.125rem;
  }
}

.changelog-content {
  width: 100%;
  max-width: 56rem;
  margin: 0 auto;
}

.changelog-tags {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.tag-card {
  width: 100%;
  background-color: #1f2937;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid #374151;
}

.tag-header {
  display: flex;
  gap: 0.5rem;
  justify-items: start;
  align-items: center;
  padding: 1rem 1.25rem;
  background-color: #374151;
  border-bottom: 1px solid #4b5563;

  .tag-icon {
    width: 2.5rem;
    height: 2.5rem;
    padding: 0.5rem;
    border-radius: 5rem;
    color: hsla(0, 0%, 100%, 0.75);
    border: 1px solid hsla(0, 0%, 100%, 0.25);
  }

  .tag-info {
    display: flex;
    flex-direction: column;

    .tag-title {
      text-align: left;
      font-size: 1.25rem;
      font-weight: 700;
      color: white;
      line-height: 1.25rem;
    }

    .tag-date {
      font-size: 0.7rem;
      color: hsla(0, 0%, 100%, 0.7);
    }
  }
}

.issues-list {
  list-style: none;
  margin: 0;
  padding: 0;

  .no-issues {
    padding: 1rem 1.5rem;
    color: #9ca3af;
  }
}

.issue-item {
  padding: 1rem 1.5rem;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #374151;

  &:hover {
    background-color: rgba(55, 65, 81, 0.5);
  }

  &:last-child {
    border-bottom: none;
  }
}

.issue-content {
  display: flex;
  align-items: flex-start;
}

.issue-avatar {
  flex-shrink: 0;
  margin-right: 0.75rem;

  .avatar-text {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background-color: #4b5563;
    font-size: 0.75rem;
    font-weight: 500;
    color: white;
  }
}

.issue-details {
  flex: 1;

  .issue-title {
    font-weight: 500;
    color: white;
    margin: 0 0 0.25rem 0;
    line-height: 1.4;
    text-align: initial;
  }

  .issue-meta {
    display: flex;
    align-items: center;
    font-size: 0.75rem;
    color: #9ca3af;
    gap: 0.75rem;

    .issue-author {
      margin: 0;
    }

    .issue-link {
      color: #60a5fa;
      text-decoration: none;
      transition: color 0.2s ease;
      display: flex;
      align-items: center;
      gap: 0.25rem;

      &:hover {
        color: #93c5fd;
      }

      .link-icon {
        width: 0.875rem;
        height: 0.875rem;
      }
    }
  }
}

.load-more-section {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  padding-bottom: 4rem;

  .load-more-btn {
    padding: 0.75rem 1.5rem;
    background-color: #2563eb;
    color: white;
    border-radius: 0.5rem;
    border: none;
    transition: background-color 0.2s ease;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 500;

    &:hover:not(:disabled) {
      background-color: #1d4ed8;
    }

    &:focus {
      outline: 2px solid #3b82f6;
      outline-offset: 2px;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// Responsive Design
@media (max-width: 768px) {
  .changelog-container {
    padding: 1rem;
  }

  .tag-header {
    padding: 0.50rem 0.75rem;

    .tag-info {
      .tag-title {
        font-size: 1rem;
      }

      .tag-date {
        font-size: 0.6rem;
      }
    }
  }

  .issue-item {
    padding: 0.75rem 1rem;
  }

  .issue-content {
    flex-direction: column;
    gap: 0.5rem;

    .issue-avatar {
      margin-right: 0;
      align-self: flex-start;
    }
  }

  .issue-meta {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 0.5rem !important;
  }
}
</style>
